import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useTemplateModel } from '@/store/models/template-model';
import './dynamic-bg.less';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { Button, Select, SliderInput } from '@meitu/saas-ui';
import { getImgInfoAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import {
    DynamicType,
    IBackgroundLayerProps,
    IDynamicInfoProps,
    IShaderInfoProps,
} from '@meitu/saas-editor-core/es/version-types';
import ImageUpload from '@meitu/saas-editor-ui/es/components/image-upload';
import ColorGroup from '@meitu/xdesign-widgets/dist/colorGroup';
import { editor } from '@/instance';
import { getPPTDynamicBackgroundList } from '@/services/ai';

export interface IDynamicShaderListItemType {
    name: string;
    key: string;
    preview: string;
    type: DynamicType;
    formula_file: string;
}

const getDefaultShaderInfo = () => {
    return {
        shaderUrl: '',
        backgroundColor: '',
        speed: 50,
        position: { x: 0.5, y: 0.5 },
        color: '',
    };
};
const getDefaultDynamicInfo = () => {
    return {
        name: '',
        screenshotUrl: '',
        dynamicType: DynamicType.Shader,
        shaderInfo: getDefaultShaderInfo(),
        hidden: false,
    };
};
const DynamicBg: React.FC<{
    setCurrent: Function;
}> = ({ setCurrent }) => {
    const backgroundLayer = editor.backgroundLayer.getBackgroundLayer();

    const [shaderList, setShaderList] = useState<IDynamicShaderListItemType[]>([]);

    const extraInfo = useTemplateModel((state) => state.extraInfo);
    const setExtraInfo = useTemplateModel((state) => state.setExtraInfo);
    const [dynamicInfo, setDynamicInfo] = useState<IDynamicInfoProps>(getDefaultDynamicInfo());

    const imageUploadProps: any = {
        convertToWebp: false,
        children: (
            <>
                <Button type="primary" data-blue onClick={() => {}}>
                    上传预览图
                </Button>
            </>
        ),
        onSuccess: (url: string) => {
            setExtraInfo({
                ...extraInfo,
                dynamicBgPreviewUrl: url,
            });
        },
    };
    useEffect(() => {
        const dynamicInfo = (backgroundLayer?.data as IBackgroundLayerProps)?.dynamicInfo;
        if (!dynamicInfo) {
            setDynamicInfo(getDefaultDynamicInfo());
        } else {
            setDynamicInfo(dynamicInfo);
        }
    }, [backgroundLayer]);

    useEffect(() => {
        if (backgroundLayer && (backgroundLayer.data as IBackgroundLayerProps)?.dynamicInfo) {
            setDynamicInfo((backgroundLayer.data as IBackgroundLayerProps).dynamicInfo!);
        }
        const run = async () => {
            const rsp = await getPPTDynamicBackgroundList();
            const list = (rsp.data as any).data.data;

            setShaderList(
                list.map((item: any) => {
                    const { dynamic_background_type, name, formula_file, preview } = item.meta;
                    return {
                        name: item.default_name,
                        key: name,
                        preview: preview.url,
                        type: dynamic_background_type,
                        formula_file: formula_file.url,
                    };
                }),
            );
        };
        run();
    }, []);

    const handleGlobalApply = () => {
        console.log('全局应用');
        const stages = editor.services.frame.getFrameIdsLength();
        for (let i = 0; i < stages; i++) {
            const bg = editor.backgroundLayer.getBackgroundLayer(i);
            if (!bg) continue;
            bg.data = cloneDeep(backgroundLayer?.data) as IBackgroundLayerProps;
            bg.data = {
                ...bg.data,
                dynamicInfo: {
                    ...dynamicInfo,
                },
            } as IBackgroundLayerProps;
            editor.backgroundLayer.update(bg!, true);
        }
    };

    const updateDynamicInfo = async (info: IDynamicInfoProps, pushHistory = true) => {
        if ((backgroundLayer?.data as IBackgroundLayerProps).url !== info.screenshotUrl) {
            (backgroundLayer?.data as IBackgroundLayerProps).url = info.screenshotUrl;
            const { width, height } = await getImgInfoAsync(info.screenshotUrl);
            const { widthRatio, heightRatio } = computerBgRatio({
                imgWidth: width,
                imgHeight: height,
                stageHeight: backgroundLayer?.height || 0,
                stageWidth: backgroundLayer?.width || 0,
            });

            (backgroundLayer!.data as IBackgroundLayerProps) = {
                ...(backgroundLayer?.data as IBackgroundLayerProps),
                backgroundColor: '',
                naturalHeight: height,
                naturalWidth: width,
                imageTransform: {
                    offsetX: 0,
                    offsetY: 0,
                    rotate: 0,
                    scale: 1,
                    flipH: false,
                    flipV: false,
                    widthRatio,
                    heightRatio,
                },
            };

            const dynamicInfo = cloneDeep(info);

            const newLayer = editor.backgroundLayer.generateLayer(backgroundLayer!, {
                ...backgroundLayer!.data,
                dynamicInfo,
            });

            setCurrent({ dynamicInfo });

            editor.backgroundLayer.update(newLayer, pushHistory, undefined, backgroundLayer!);
        }
    };

    if (!backgroundLayer) {
        return null;
    }

    return (
        <div className="c-dynamic-bg-wrap">
            <div className="label-wrap">
                <LabelAction title="动态背景:">
                    <Select
                        style={{ width: '100%' }}
                        value={dynamicInfo.name}
                        onChange={(value, option: any) => {
                            if (option.type === DynamicType.Shader) {
                                const info = {
                                    ...dynamicInfo,
                                    name: value,
                                    screenshotUrl: option.screenshotUrl,
                                    shaderInfo: {
                                        ...getDefaultShaderInfo(),
                                        shaderUrl: option.url,
                                    },
                                    dynamicType: DynamicType.Shader,
                                    hidden: false,
                                };
                                setDynamicInfo(info);
                                updateDynamicInfo(info);
                            } else {
                                const info = {
                                    ...dynamicInfo,
                                    name: value,
                                    screenshotUrl: option.screenshotUrl,
                                    dynamicType: DynamicType.CSS,
                                    hidden: false,
                                };
                                setDynamicInfo(info);
                                updateDynamicInfo(info);
                            }
                        }}
                        options={shaderList.map((item) => {
                            return {
                                value: item.key,
                                label: item.name,
                                screenshotUrl: item.preview,
                                type: item.type,
                                url: item.formula_file,
                            };
                        })}
                    />
                </LabelAction>
            </div>

            <div className="label-wrap">
                <LabelAction title="预览图:">
                    <ImageUpload {...imageUploadProps} />
                </LabelAction>
            </div>

            <div className="label-wrap">
                {extraInfo?.dynamicBgPreviewUrl ? (
                    <img style={{ width: '100%', height: 'auto' }} src={extraInfo?.dynamicBgPreviewUrl} alt="" />
                ) : null}
            </div>

            {dynamicInfo &&
            dynamicInfo.dynamicType === DynamicType.Shader &&
            dynamicInfo.shaderInfo &&
            dynamicInfo.shaderInfo?.shaderUrl &&
            !dynamicInfo.hidden ? (
                <>
                    <div className="label-wrap">
                        <LabelAction title="动画速度">
                            <div
                                className="text-action__item"
                                style={{
                                    width: '158px',
                                }}
                            >
                                <SliderInput
                                    value={dynamicInfo.shaderInfo.speed || 0}
                                    max={100}
                                    min={1}
                                    showStepBtn={true}
                                    type="horizontal"
                                    onChange={(val: number) => {
                                        const info = {
                                            ...dynamicInfo,
                                            shaderInfo: {
                                                ...(dynamicInfo.shaderInfo as IShaderInfoProps),
                                                speed: val,
                                            },
                                            dynamicType: DynamicType.Shader,
                                            hidden: false,
                                        };
                                        setDynamicInfo(info);
                                        updateDynamicInfo(info, false);
                                    }}
                                    onAfterChange={(val: number) => {
                                        const info = {
                                            ...dynamicInfo,
                                            shaderInfo: {
                                                ...(dynamicInfo.shaderInfo as IShaderInfoProps),
                                                speed: val,
                                            },
                                            dynamicType: DynamicType.Shader,
                                            hidden: false,
                                        };
                                        setDynamicInfo(info);
                                        updateDynamicInfo(info);
                                    }}
                                />
                            </div>
                        </LabelAction>
                    </div>
                    <div className="label-wrap">
                        <LabelAction title="背景颜色">
                            <ColorGroup
                                color={dynamicInfo?.shaderInfo?.backgroundColor || ''}
                                setColor={(val: string) => {
                                    const info = {
                                        ...dynamicInfo,
                                        shaderInfo: {
                                            ...(dynamicInfo.shaderInfo as IShaderInfoProps),
                                            backgroundColor: val,
                                        },
                                        dynamicType: DynamicType.Shader,
                                        hidden: false,
                                    };
                                    setDynamicInfo(info);
                                    updateDynamicInfo(info, false);
                                }}
                                setColorAfter={(val: string) => {
                                    const info = {
                                        ...dynamicInfo,
                                        shaderInfo: {
                                            ...(dynamicInfo.shaderInfo as IShaderInfoProps),
                                            backgroundColor: val,
                                        },
                                        dynamicType: DynamicType.Shader,
                                        hidden: false,
                                    };
                                    setDynamicInfo(info);
                                    updateDynamicInfo(info);
                                }}
                            />
                        </LabelAction>{' '}
                    </div>
                </>
            ) : null}

            {/* <div className="operate">
                <Button type="primary" onClick={handleGlobalApply}>
                    全局应用
                </Button>
            </div> */}
        </div>
    );
};

export default DynamicBg;
